import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Feedback as FeedbackType, Game, Profile } from '../types';
import {
  Search,
  Calendar,
  MessageSquare,
  Send,
  Inbox,
  Users,
} from 'lucide-react';
import FeedbackList from '../components/FeedbackList';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';

export default function Feedback() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const [feedback, setFeedback] = useState<FeedbackType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const [games, setGames] = useState<Record<string, Game>>({});
  const [gameOwners, setGameOwners] = useState<Record<string, Profile>>({});
  const [viewMode, setViewMode] = useState<'received' | 'sent'>('received');

  const fetchFeedback = async () => {
    if (!user) return;

    try {
      // First, fetch all games for the user
      const gamesQuery = query(
        collection(db, 'games'),
        where('userId', '==', user.uid)
      );
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesMap: Record<string, Game> = {};
      gamesSnapshot.docs.forEach((doc) => {
        gamesMap[doc.id] = {
          id: doc.id,
          ...doc.data(),
          date: new Date(doc.data().date),
          createdAt: new Date(doc.data().createdAt),
        } as Game;
      });
      setGames(gamesMap);

      // Then fetch feedback based on view mode
      let feedbackQuery;
      if (viewMode === 'received') {
        // Fetch feedback for user's games
        const gameIds = Object.keys(gamesMap);
        if (gameIds.length === 0) {
          setFeedback([]);
          setLoading(false);
          return;
        }

        feedbackQuery = query(
          collection(db, 'feedback'),
          where('gameId', 'in', gameIds),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Fetch feedback given by the user
        feedbackQuery = query(
          collection(db, 'feedback'),
          where('authorId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
      }

      const feedbackSnapshot = await getDocs(feedbackQuery);
      const feedbackData = feedbackSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
      })) as FeedbackType[];

      // If in "sent" mode, fetch profiles of game owners and their games
      if (viewMode === 'sent') {
        const uniqueGameIds = [...new Set(feedbackData.map((f) => f.gameId))];
        const gameOwnersMap: Record<string, Profile> = {};
        const gamesMap: Record<string, Game> = {};

        for (const gameId of uniqueGameIds) {
          const gameDoc = await getDoc(doc(db, 'games', gameId));
          if (gameDoc.exists()) {
            const gameData = gameDoc.data();
            gamesMap[gameId] = {
              id: gameId,
              ...gameData,
              date: new Date(gameData.date),
              createdAt: new Date(gameData.createdAt),
            } as Game;

            const ownerDoc = await getDoc(doc(db, 'profiles', gameData.userId));
            if (ownerDoc.exists()) {
              gameOwnersMap[gameId] = {
                id: ownerDoc.id,
                ...ownerDoc.data(),
                createdAt: ownerDoc.data().createdAt.toDate(),
              } as Profile;
            }
          }
        }

        setGameOwners(gameOwnersMap);
        setGames(gamesMap);
      }

      setFeedback(feedbackData);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(t('feedback.failedToLoadFeedback'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [user, viewMode]);

  const filteredFeedback = feedback.filter((item) => {
    const matchesSearch =
      searchTerm === '' ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (games[item.gameId]?.opponent || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const itemDate = item.createdAt;
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    if (endDate) endDate.setHours(23, 59, 59, 999);

    const matchesDateRange =
      (!startDate || itemDate >= startDate) &&
      (!endDate || itemDate <= endDate);

    return matchesSearch && matchesDateRange;
  });

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4'></div>
          <p className='text-gray-600'>{t('feedback.loadingFeedback')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto px-4 py-8'>
      <div className='flex items-center justify-between mb-8'>
        <h1 className='text-2xl md:text-3xl font-bold text-primary-600 flex items-center gap-2 italic'>
          <MessageSquare className='h-7 w-7' />
          {t('feedback.title')}
        </h1>
        <Link
          to='/goalie-coaches'
          className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2'
        >
          <Users className='h-5 w-5' />
          <span>{t('feedback.hireACoach')}</span>
        </Link>
      </div>

      {error ? (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {error}
        </div>
      ) : (
        <>
          <div className='glass-card p-6 mb-8'>
            <div className='flex flex-col md:flex-row gap-4 md:items-center mb-6'>
              <div className='flex rounded-lg border border-gray-300 p-1'>
                <button
                  onClick={() => setViewMode('received')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'received'
                      ? 'bg-[#1b3968] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Inbox className='h-4 w-4' />
                  {t('feedback.received')}
                </button>
                <button
                  onClick={() => setViewMode('sent')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'sent'
                      ? 'bg-[#1b3968] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Send className='h-4 w-4' />
                  {t('feedback.sent')}
                </button>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('feedback.searchFeedback')}
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('feedback.searchPlaceholder')}
                    className='pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  />
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  {t('feedback.dateRange')}
                </label>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='relative'>
                    <input
                      type='date'
                      value={dateRange.start}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          start: e.target.value,
                        }))
                      }
                      className='pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    />
                    <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
                  </div>
                  <div className='relative'>
                    <input
                      type='date'
                      value={dateRange.end}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          end: e.target.value,
                        }))
                      }
                      className='pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    />
                    <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='space-y-6'>
            <FeedbackList
              feedback={filteredFeedback}
              onFeedbackDeleted={fetchFeedback}
              viewMode={viewMode}
              gameOwners={gameOwners}
              games={games}
            />
          </div>
        </>
      )}
    </div>
  );
}